//#region [IMPORT]

import { useEffect, useState } from "react";

//#endregion

const visibility_targets = new Map();
let visibility_observer = null;

//-----------------------------------------------------------------
function getVisibilityObserver() {
  //-----------------------------------------------------------------
  if( visibility_observer ) return visibility_observer;

  visibility_observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if( !entry.isIntersecting ) return;

        const on_visible = visibility_targets.get(entry.target);
        if( on_visible ) on_visible();
      });
    },
    {
      threshold: 0.1,
      rootMargin: "100% 0px 100% 0px",
      scrollMargin: "100% 0px 100% 0px",
    },
  );

  return visibility_observer;
}

//-----------------------------------------------------------------
function observeVisibility(element, on_visible) {
  //-----------------------------------------------------------------
  const observer = getVisibilityObserver();
  visibility_targets.set(element, on_visible);
  observer.observe(element);

  return () => {
    observer.unobserve(element);
    visibility_targets.delete(element);

    if( visibility_targets.size === 0 ) {
      observer.disconnect();
      if( visibility_observer === observer ) visibility_observer = null;
    }
  };
}

//-----------------------------------------------------------------
function isInScrollViewport(element) {
  //-----------------------------------------------------------------
  if( !element?.getBoundingClientRect ) return false;

  const rect = element.getBoundingClientRect();
  const viewport_width = window.innerWidth || document.documentElement.clientWidth;
  const viewport_height = window.innerHeight || document.documentElement.clientHeight;

  if(
    rect.bottom <= 0 || rect.top >= viewport_height ||
    rect.right <= 0 || rect.left >= viewport_width
  ) return false;

  // IntersectionObserver normally accounts for clipping ancestors, but large
  // programmatic scroll jumps can leave its callback pending. Verify that the
  // element is also inside every scrollable ancestor before loading it.
  let parent = element.parentElement;
  while( parent && parent !== document.body ) {
    const style = window.getComputedStyle(parent);
    const overflow = `${style.overflow} ${style.overflowX} ${style.overflowY}`;

    if( /(auto|scroll|hidden|clip)/.test(overflow) ) {
      const parent_rect = parent.getBoundingClientRect();
      if(
        rect.bottom <= parent_rect.top || rect.top >= parent_rect.bottom ||
        rect.right <= parent_rect.left || rect.left >= parent_rect.right
      ) return false;
    }

    parent = parent.parentElement;
  }

  return true;
}

//-----------------------------------------------------------------
export default function useOnVisible(ref) {
  //-----------------------------------------------------------------
  //#region [VALUES]

  const [is_visible, setIsVisible] = useState(false); // the state to store the fetched data
  
  //#endregion

  //#region [EFFECT]

  useEffect(() => {
    if( is_visible ) return;

    // A missing ref cannot be observed later because the ref object itself does
    // not change and therefore does not re-run this effect. Prefer loading the
    // content over leaving a permanently empty lazy-load placeholder.
    if( !ref?.current ) {
      setIsVisible(true);
      return;
    }

    const element = ref.current;

    // Do not wait for an asynchronous observer callback when the field is
    // already visible. This is important after focus scrolls very large tasks.
    if( isInScrollViewport(element) ) {
      setIsVisible(true);
      return;
    }

    // Large tasks can contain thousands of lazy fields. A single observer is
    // substantially more reliable and efficient than one observer per field.
    return observeVisibility(element, () => setIsVisible(true));
  }, [ref, is_visible]); // the dependencies of the effect

  //#endregion

  return [is_visible];
};
